import { useEffect, useMemo, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import UserCardContent from 'components/UI/UserCard/UserCardContent'
import {
  FeedAnalyticsCardContent,
  FeedAnalyticsCardDropdown
} from 'components-ref/organisms/analytics/content'
import { getContentFeedAnalytics } from 'services/endpoints/analytic'
import { useAnalyticsContentCacheStore } from 'store/analyticsContentCacheStore'
import { type ContentFilter } from 'types/api/analytic'
import { type ContentModalSortFilter } from 'types/interfaces/IContentModalSortFilter'

interface FeedAnalyticsProps {
  selectedCategory?: string
  filter?: ContentFilter
  sort: ContentModalSortFilter
  onLoaded?: () => void
}

export function FeedAnalytics({ selectedCategory, filter, sort, onLoaded }: FeedAnalyticsProps) {
  const { ref, inView } = useInView({})
  const { hydrated, getCache, setCache } = useAnalyticsContentCacheStore()
  const [ready, setReady] = useState(false)
  const [fallbackData, setFallbackData] = useState<any[] | null>(null)
  const [enabled, setEnabled] = useState(false)

  const normalizedCategoryId = selectedCategory || undefined

  const cacheKey = useMemo(() => {
    return `feed:${normalizedCategoryId ?? 'all'}:${JSON.stringify(filter)}:${JSON.stringify(sort)}`
  }, [normalizedCategoryId, filter, sort])

  const queryKey = useMemo(
    () => [
      'contentAnalytics',
      'feed',
      normalizedCategoryId ?? 'all',
      sort,
      JSON.stringify(filter)
    ],
    [normalizedCategoryId, sort, filter]
  )

  // Hydrate from Zustand cache
  useEffect(() => {
    if (!hydrated) return
    const cached = getCache(cacheKey)
    if (cached?.data?.length) {
      setFallbackData(cached.data)
    }
    setEnabled(true)
    setReady(true)
  }, [hydrated, getCache, cacheKey])

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetching,
    hasNextPage
  } = useInfiniteQuery<any>(
    queryKey,
    async ({ pageParam = 1 }) => {
      return await getContentFeedAnalytics({
        categoryId: selectedCategory ?? undefined,
        filter,
        pageParam,
        sort
      })
    },
    {
      getNextPageParam: lastPage => lastPage?.nextCursor,
      enabled,
      staleTime: 60_000,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  )

  // Save fresh data to Zustand cache
  useEffect(() => {
    if (data && !isFetching) {
      const all = data?.pages?.flatMap?.(p => Array.isArray(p?.data) ? p.data : []) ?? []
      setCache(cacheKey, {
        data: all,
        lastFetched: Date.now()
      })
      setFallbackData(all)
    }
  }, [data, isFetching, cacheKey, setCache])

  // Pagination observer -- **add hasNextPage guard**
  useEffect(() => {
    if (inView && hasNextPage && !isFetching && !isLoading) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetching, isLoading, fetchNextPage])

  useEffect(() => {
    if (!ready) return

    const hasCachedData = !!fallbackData?.length
    const hasFreshData = !!data?.pages?.length
    const finishedEmptyFreshLoad = enabled && !isLoading && !isFetching

    if (hasCachedData || hasFreshData || finishedEmptyFreshLoad) {
      onLoaded?.()
    }
  }, [
    ready,
    fallbackData?.length,
    data?.pages?.length,
    enabled,
    isLoading,
    isFetching,
    onLoaded
  ])


  if (!ready) return null

  const hasCached = !!fallbackData?.length
  const isFirstLoadWithoutCache = !hasCached && isLoading

  if (isFirstLoadWithoutCache) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '2rem 0'
        }}
      >
        <BeatLoader size={8} />
      </div>
    )
  }
  // Defensive flattening to avoid crashes
  const allCards =
    data?.pages?.flatMap?.(p => Array.isArray(p?.data) ? p.data : []) ?? fallbackData ?? []


  return (
    <>
      {allCards.map((card: any, index: number) => (
        <span
          // Only attach ref if there *is* a next page to fetch
          ref={hasNextPage && index === allCards.length - 1 ? ref : null}
          key={index}
        >
          <UserCardContent
            thumb_src={card.photos[0]?.url ?? card.videos[0]?.active_thumb ?? ''}
            cardContent={<FeedAnalyticsCardContent card={card} sort={sort} index={index} />}
            hasDropDown={true}
            dropDownContent={<FeedAnalyticsCardDropdown card={card} />}
            card={card}
            customIconClass='content-icon-square'
            iconSize='80'
          />
        </span>
      ))}
    </>
  )
}
